import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import type { DataState, ProviderWithId } from "../types";
import type { AuthAppDispatch, AuthRootState } from "./store";

/**
 * Error message dispatched when a provider document does not exist in Firestore.
 *
 * Consumers compare against this exact string to distinguish a "provider not
 * found" state from other listener/network errors.
 */
export const PROVIDER_DETAILS_NOT_FOUND_ERROR = "Provider not found";

const initialState: DataState<ProviderWithId> = {
  data: null,
  loading: false,
  error: null,
};

const providerDetailsSlice = createSlice({
  name: "providerDetails",
  initialState,
  reducers: {
    getProviderDetailsStart(state) {
      state.loading = true;
      state.error = null;
      state.data = null;
    },
    getProviderDetailsSuccess(state, action: PayloadAction<ProviderWithId>) {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    getProviderDetailsFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
    },
    resetProviderDetails(state) {
      state.data = null;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  getProviderDetailsStart,
  getProviderDetailsSuccess,
  getProviderDetailsFailure,
  resetProviderDetails,
} = providerDetailsSlice.actions;

/**
 * Thunk that subscribes to a provider's Firestore document and syncs it into state.
 *
 * Skips dispatching when a fetch is already in flight. On each snapshot it
 * dispatches success with the provider data, or failure with
 * {@link PROVIDER_DETAILS_NOT_FOUND_ERROR} when the document is missing.
 *
 * @param id - The provider (user) id whose document to listen to
 * @returns A thunk resolving to the Firestore listener's unsubscribe function
 */
export const getProviderDetailsFor =
  (id: string) =>
    async (dispatch: AuthAppDispatch, getState: () => AuthRootState) => {
      const { providerDetails } = getState();
      if (providerDetails.loading) {
        return;
      }
      dispatch(getProviderDetailsStart());
      const unsubscribe = FirestoreService.listenToDocument<ProviderWithId>(
        FirestoreCollectionReference.providers(),
        id,
        (providerData) => {
          if (providerData) {
            dispatch(getProviderDetailsSuccess(providerData));
          } else {
            dispatch(getProviderDetailsFailure(PROVIDER_DETAILS_NOT_FOUND_ERROR));
          }
        },
        (error) => {
          dispatch(getProviderDetailsFailure(error.message));
        },
      );

      return unsubscribe;
    };

export default providerDetailsSlice.reducer;
